import { Fragment, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { dateCheck } from '@utils/cookies';
import * as S from './styles';
import BlackFridayBar from './BlackFridayBar';
import { bannerData, isBlackFriday } from './bannerData';

export default function Banner() {
  const router = useRouter();
  const [testBanner, setTestBanner] = useState<string | null>(null);
  const [showAllBanners, setShowAllBanners] = useState(false);
  const [now, setNow] = useState(0);

  const excludedRoutes = ['/live', '/oppo'];
  const currentPath = router?.pathname || '';
  const shouldHideBanner = excludedRoutes.includes(currentPath);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const bannerTest = params.get('banner-test');
      const showAll = params.get('banner-show-all');

      setTestBanner(bannerTest);
      setShowAllBanners(showAll === 'true');
    }
  }, []);

  // Re-renderiza quando a próxima borda da escala passa, para a fase trocar em aba aberta.
  // Acorda no máximo a cada 1h: setTimeout acima de ~24,8 dias estoura e vira 1ms.
  useEffect(() => {
    const current = Date.now();
    const next = bannerData
      .flatMap(banner => [Date.parse(banner.startDate), Date.parse(banner.endDate) + 1000])
      .filter(edge => edge > current)
      .sort((a, b) => a - b)[0];
    if (!next) return undefined;

    const timer = setTimeout(() => setNow(Date.now()), Math.min(next - current + 250, 60 * 60 * 1000));
    return () => clearTimeout(timer);
  }, [now]);

  if (shouldHideBanner) {
    return null;
  }

  return (
    <>
      {bannerData.map(banner => {
        const isTestMode = testBanner === banner.name || showAllBanners;
        const bannerIsInactive = !dateCheck(banner.startDate, banner.endDate, new Date());
        const shouldShowBanner = isTestMode || !bannerIsInactive;

        if (isBlackFriday(banner)) {
          return shouldShowBanner && <BlackFridayBar key={banner.name} banner={banner} isTestMode={isTestMode} />;
        }

        return (
          <Fragment key={banner.name}>
            {shouldShowBanner && (
              <section
                style={{ background: banner.color.barColors }}
                className="fixed left-0 top-0 z-[988] mt-[60px] h-[50px] min-h-[50px] w-full overflow-hidden p-0 pl-0 text-mglGray50 md:mt-0 tablet:pl-[60px] [&+div+div]:mt-[50px] [&+div]:mt-[50px] [&_svg]:ml-[10px] [&_svg]:cursor-pointer">
                <div className="mx-auto my-0 flex h-full w-full max-w-full basis-full items-center justify-center px-[0.625rem] tablet:max-w-[83.3333%] tablet:basis-[83.3333%] desktop:px-0">
                  {isTestMode && (
                    <div className="bg-red-500 absolute left-2 top-1 rounded px-2 py-1 text-[10px] font-bold text-white">TESTE: {banner.name}</div>
                  )}
                  <div className="hidden items-center px-0 py-2 desktop:flex">
                    <strong style={{ color: banner.color.textBackground }} className="hidden items-center whitespace-pre-wrap font-medium desktop:flex">
                      {banner.emphasis?.md && <strong className="p-2">{banner.emphasis.md}</strong>} {banner.texts.md}
                    </strong>
                    <S.VoteButton href={banner.link.url} target="_blank" title={banner.texts.md}>
                      <strong style={{ color: banner.color.textButtonColor }}>{banner.texts.buttonText}</strong>
                    </S.VoteButton>
                  </div>
                  <div className="hidden w-full items-center py-2 tablet:flex desktop:hidden">
                    <S.VoteButton href={banner.link.url} target="_blank" title={banner.texts.sm}>
                      <strong style={{ color: banner.color.textButtonColor }}>{banner.emphasis?.sm || banner.texts.sm}</strong>
                    </S.VoteButton>
                  </div>
                  <div className="flex w-full items-center px-0 py-2 tablet:hidden">
                    <S.VoteButton href={banner.link?.url} target="_blank" title={banner.texts?.xs}>
                      <strong style={{ color: banner.color.textButtonColor }}>{banner.emphasis?.xs || banner.texts?.xs}</strong>
                    </S.VoteButton>
                  </div>
                </div>
              </section>
            )}
          </Fragment>
        );
      })}
    </>
  );
}
